'use client';
import { GetInfoItemsResult, InfoBarModule, InfoBarPriority } from './InfoBarModule';
import MiniSnapshotV3 from '../../../utils/MiniSnapshotV3';
import React, { ReactNode } from 'react';
import styled from '@benzinga/themetron';
import {
  HorizontalRuleModule,
  ProTickerPopover,
  QuoteData,
  SparkLineModule,
  TickerHeaderModule,
  TickerNotesModule,
} from '../../TickerHover';
import { noop } from '@benzinga/utils';

const TickerQuoteWidth = 275;
export class TickerQuotesModule extends InfoBarModule {
  public id = 'ticker-quotes';
  public priority = InfoBarPriority.Base;
  public getInfoItems: Generator<GetInfoItemsResult, GetInfoItemsResult, number>;
  public name = 'Ticker Quotes';
  private symbolNodes: ReactNode[] = [];
  private onClick: (id: string) => void;

  constructor(onClick?: (id: string) => void) {
    super();
    this.onClick = onClick ?? noop;
    this.getInfoItems = this.tickerItemsGenerator();
  }

  public updateTickerList(newSymbols: string[]) {
    const { onClick, symbolNodes } = this;
    symbolNodes.splice(
      0,
      symbolNodes.length,
      ...newSymbols.map(symbol => (
        <ProTickerPopover
          key={symbol}
          modules={
            <>
              <TickerHeaderModule onSymbolClick={onClick} />
              <HorizontalRuleModule />
              <SparkLineModule />
              <HorizontalRuleModule />
              <QuoteData symbol={symbol} />
              <HorizontalRuleModule />
              <TickerNotesModule />
            </>
          }
          positions={['top']}
          symbol={symbol}
        >
          <TickerQuoteWrapper onClick={() => onClick?.(symbol)}>
            <MiniSnapshotV3 symbol={symbol} />
          </TickerQuoteWrapper>
        </ProTickerPopover>
      )),
    );
  }

  *tickerItemsGenerator(): Generator<GetInfoItemsResult, GetInfoItemsResult, number> {
    let index = 0;
    let infoBarWidth = yield { items: [] };

    while (true) {
      let totalWidth = 0;
      const { symbolNodes } = this;

      infoBarWidth = yield symbolNodes.reduce<GetInfoItemsResult>(
        // eslint-disable-next-line no-loop-func
        (acc, _0, _1, arr) => {
          if (totalWidth + TickerQuoteWidth < infoBarWidth) {
            acc.items.push({ node: arr[index], width: TickerQuoteWidth });
            index = (index + 1) % arr.length;
            totalWidth += TickerQuoteWidth;
          }
          return acc;
        },
        { items: [] },
      );
    }
  }
}

export const useTickerQuotesModule = (symbols: string[], onClick: (symbol: string) => void): TickerQuotesModule => {
  const module = React.useMemo<TickerQuotesModule>(() => new TickerQuotesModule(onClick), [onClick]);
  React.useEffect(() => {
    module.updateTickerList(symbols);
  }, [module, symbols]);

  return module;
};

const TickerQuoteWrapper = styled.span`
  display: flex;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 0;
  justify-content: space-around;
  cursor: ${props => (props.onClick ? 'pointer' : 'default')};
`;
